/**
 * State that starts from a value the page read, and starts again from it
 * whenever that value changes (a field's text, a row's draft).
 *
 * The reset happens while rendering, never in an effect: an effect runs after
 * the paint, so a field already editable could be typed into and then have the
 * old value put back over the typing. Here a new value replaces an edit in the
 * very render that brings it.
 *
 * `key` says when to start again (Object.is; defaults to the value when left
 * out). Pass one when the value is rebuilt on every render (its JSON) or is not
 * what should trigger the reset (`useStateFrom(null, load.data)`). A key passed
 * as undefined is a key like any other: `JSON.stringify(undefined)` is undefined,
 * and falling back to a value rebuilt on every render would reset it forever.
 */

import { useState, type Dispatch, type SetStateAction } from "react";

export function useStateFrom<T>(value: T, ...keyArg: [key?: unknown]): [T, Dispatch<SetStateAction<T>>] {
  const key = keyArg.length === 0 ? value : keyArg[0];
  // Updater forms throughout, so a function is kept as a value, never called.
  const [state, setState] = useState<T>(() => value);
  const [from, setFrom] = useState<unknown>(() => key);
  if (!Object.is(from, key)) {
    // React renders the component again at once with both, before any child or commit.
    setFrom(() => key);
    setState(() => value);
    return [value, setState];
  }
  return [state, setState];
}
